import { getMe, MeUser } from '@sp/data/bif';
import { isNotNullish, Nullable } from '@sp/util/helpers';
import { identifyUser } from '@sp/util/native';
import { createEffect, createEvent, createStore, forward, sample } from 'effector';
import { createGate } from 'effector-react';

const nullUser: MeUser = {
  id: -1,
  created_at: 0,
  name: 'null user',
  email: null,
  isAdmin: false,
  isExpert: false,
  description: '',
  bio: '',
  isRegistrationCompleted: false,
  isFirstChallengeVisited: false,
  slug: 'null_user',
  subscriptionPrice: null,
  avatar: null,
  pinnedChatsIds: null,
  notifications: {
    newMessagesInChallenges: false,
    newMentionsInChallenges: false,
    newCreatorsMessagesInChallenges: false,
    newMessagesInDMs: false,
    newPlatformMessages: false,
    newRepliesInChallenges: false,
  },
  paymentMethod: null,
  archivedChatsIds: null,
};

const UserGate = createGate<Nullable<MeUser>>();

const updateUserInfo = createEvent<Partial<MeUser>>();
const refreshStarted = createEvent();

const identifyUserFx = createEffect((user: MeUser) => {
  identifyUser(user.id.toString());
});

const refreshUserInfoFx = createEffect(() => getMe({ force: true }));

const $meUser = createStore<MeUser>(nullUser)
  .on(updateUserInfo, (state, user) => ({ ...state, ...user }))
  .on(refreshUserInfoFx.doneData, (_, user) => user);

sample({ source: UserGate.state, target: $meUser, filter: isNotNullish });

sample({
  source: $meUser,
  filter: user => user.id !== -1,
  target: identifyUserFx,
});

forward({ from: refreshStarted, to: refreshUserInfoFx });

const $isAuthorised = UserGate.state.map(user => user != null && user.id > 0);
const $isRefreshing = refreshUserInfoFx.pending;
const refreshSuccess = refreshUserInfoFx.done;

export const USER_MODEL = {
  UserGate,
  $meUser,
  $isAuthorised,
  updateUserInfo,
  refreshStarted,
  refreshSuccess,
  $isRefreshing,
};
